import * as React from 'react';
import * as Cx from 'classnames';
import { connect } from 'react-redux';
const { firebaseConnect } = require('react-redux-firebase');

import { RootState, Rsvp, RsvpYes, RsvpNo, Message } from '../reducers/root';
import Modal from './modal';
import RsvpForm from './forms/rsvpForm';
import SendMessageForm from './forms/sendMessageForm';

interface RsvpMap {
  [id: string]: Rsvp;
}

interface AdminDashboardStateProps {
  rsvps: RsvpMap;
}

interface AdminDashboardProps extends AdminDashboardStateProps {
  firebase: {
    push: (path: string, data: Rsvp | Message) => Promise<any>; // tslint:disable-line:no-any
    remove: (path: string) => void;
  };
}

type Tab = 'yes' | 'no' | 'add' | 'message';

interface SplitYesNoRsvps {
  yes: RsvpYes[];
  no: RsvpNo[];
}

interface AdminDashboardState extends SplitYesNoRsvps {
  activeTab: Tab;
  rsvpModalTarget: Rsvp | void;
}

const defaultState: AdminDashboardState = {
  activeTab: 'yes',
  rsvpModalTarget: undefined,
  yes: [],
  no: [],
};

class AdminDashboard extends React.PureComponent<AdminDashboardProps, AdminDashboardState> {
  static splitRsvps(rsvps: RsvpMap): SplitYesNoRsvps {
    const results: SplitYesNoRsvps = {
      yes: [],
      no: [],
    };
    if (rsvps) {
      Object.keys(rsvps)
        .map(id => ({ ...rsvps[id], id: id }))
        .forEach((rsvp: Rsvp) => {
          if (rsvp.attending) {
            results.yes.push(rsvp);
          } else {
            results.no.push(rsvp);
          }
        });
    }
    return results;
  }

  constructor(props: AdminDashboardProps) {
    super(props);
    this.state = {
      ...defaultState,
      ...AdminDashboard.splitRsvps(props.rsvps),
    };
  }

  componentWillReceiveProps(nextProps: AdminDashboardProps): void {
    this.setState(AdminDashboard.splitRsvps(nextProps.rsvps));
  }

  switchTab = (targetTab: Tab) => () => this.setState({ activeTab: targetTab });

  onRsvpDetails = (targetRsvp: Rsvp) => () => this.setState({ rsvpModalTarget: targetRsvp });

  onModalDismiss = () => this.setState({ rsvpModalTarget: undefined });

  onDeleteRsvp = (id: string) => (callback?: Function) => (_?: React.MouseEvent<HTMLTableDataCellElement>) => {
    if (window.confirm('Are you sure you want to delete this RSVP?')) {
      this.props.firebase.remove(`rsvps/${id}`);
      if (typeof callback === 'function') {
        callback();
      }
    }
  };

  multilineText(text: string): JSX.Element[] {
    return text.split(' //// ').map((line, idx) => <div key={idx}>{line}</div>);
  }

  rsvps(): JSX.Element {
    switch (this.state.activeTab) {
      case 'message':
        return this.sendMessage();
      case 'add':
        return this.addRsvp();
      case 'no':
        return this.noRsvps();
      case 'yes':
      default:
        return this.yesRsvps();
    }
  }

  onCreateRsvp = (rsvpData: Rsvp) => {
    this.props.firebase.push('rsvps', rsvpData);
    this.setState(() => ({ activeTab: rsvpData.attending ? 'yes' : 'no' }));
  };

  onCreateMessage = (message: Message) => {
    this.props.firebase.push('messages', message).then(ref => {
      fetch('https://sandstorm-wildebeest-8415.twil.io/sendMessage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `messageId=${ref.key}`,
        mode: 'no-cors',
      });
    });
  };

  sendMessage(): JSX.Element {
    return <SendMessageForm yesRsvps={this.state.yes} onSubmit={this.onCreateMessage} />;
  }

  addRsvp(): JSX.Element {
    return <RsvpForm onSubmit={this.onCreateRsvp} />;
  }

  yesRsvps(): JSX.Element {
    return (
      <table className="yes-table table is-fullwidth">
        <thead>
          <tr>
            <th>Name</th>
            <th>Guest</th>
            <th className="is-hidden-mobile">Phone</th>
            <th className="is-hidden-mobile">Drinks</th>
            <th className="is-hidden-mobile">Allergies</th>
            <th className="is-hidden-mobile">Address</th>
            <th className="is-hidden-mobile">Note</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {this.state.yes.map((rsvp: RsvpYes) => (
            <tr key={rsvp.id}>
              <td>{rsvp.name}</td>
              <td>{rsvp.guestName}</td>
              <td className="is-hidden-mobile">{rsvp.phoneNumber}</td>
              <td className="is-hidden-mobile">{rsvp.drinks ? 'yes' : 'no'}</td>
              <td className="is-hidden-mobile">{rsvp.allergies}</td>
              <td className="is-hidden-mobile">{this.multilineText(rsvp.address)}</td>
              <td className="is-hidden-mobile">{this.multilineText(rsvp.note)}</td>
              <td className="is-hidden-tablet has-text-right" onClick={this.onRsvpDetails(rsvp)}>
                <i className="fa fa-info-circle" />
              </td>
              <td className="is-hidden-mobile has-text-right" onClick={this.onDeleteRsvp(rsvp.id!)()}>
                <i className="fa fa-trash-o" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  noRsvps(): JSX.Element {
    return (
      <table className="no-table table is-fullwidth">
        <thead>
          <tr>
            <th>Name</th>
            <th className="is-hidden-mobile">Note</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {this.state.no.map(rsvp => (
            <tr key={rsvp.id}>
              <td>{rsvp.name}</td>
              <td className="is-hidden-mobile">{this.multilineText(rsvp.note)}</td>
              <td className="is-hidden-tablet" onClick={this.onRsvpDetails(rsvp)}>
                <i className="fa fa-info-circle has-text-right" />
              </td>
              <td className="is-hidden-mobile has-text-right" onClick={this.onDeleteRsvp(rsvp.id!)()}>
                <i className="fa fa-trash-o" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  rsvpDetails(): JSX.Element | null {
    const rsvp = this.state.rsvpModalTarget;
    if (!rsvp) {
      return null;
    }
    if (!rsvp.attending) {
      return (
        <table className="table is-fullwidth">
          <tbody>
            <tr>
              <th>RVSP</th>
              <td>Not attending</td>
            </tr>
            <tr>
              <th>Note</th>
              <td>{this.multilineText(rsvp.note)}</td>
            </tr>
          </tbody>
        </table>
      );
    }
    return (
      <table className="table is-fullwidth">
        <tbody>
          <tr>
            <th>RVSP</th>
            <td>Attending</td>
          </tr>
          <tr>
            <th>Guest</th>
            <td>{rsvp.guestName}</td>
          </tr>
          <tr>
            <th>Phone</th>
            <td>{rsvp.phoneNumber}</td>
          </tr>
          <tr>
            <th>Drinks</th>
            <td>{rsvp.drinks ? 'yes' : 'no'}</td>
          </tr>
          <tr>
            <th>Allergies</th>
            <td>{rsvp.allergies}</td>
          </tr>
          <tr>
            <th>Address</th>
            <td>{this.multilineText(rsvp.address)}</td>
          </tr>
          <tr>
            <th>Note</th>
            <td>{this.multilineText(rsvp.note)}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  render(): JSX.Element {
    return (
      <div>
        <div className="tabs is-toggle is-centered">
          <ul>
            <li className={Cx({ 'is-active': this.state.activeTab === 'yes' })}>
              <a onClick={this.switchTab('yes')}>
                <span className="icon is-small">
                  <i className="fa fa-thumbs-up" />
                </span>
                <span className="is-hidden-mobile">Attending ({this.state.yes.length})</span>
                <span className="is-hidden-tablet">{this.state.yes.length}</span>
              </a>
            </li>
            <li className={Cx({ 'is-active': this.state.activeTab === 'no' })}>
              <a onClick={this.switchTab('no')}>
                <span className="icon is-small">
                  <i className="fa fa-thumbs-down" />
                </span>
                <span className="is-hidden-mobile">Not attending ({this.state.no.length})</span>
                <span className="is-hidden-tablet">{this.state.no.length}</span>
              </a>
            </li>
            <li className={Cx({ 'is-active': this.state.activeTab === 'add' })}>
              <a onClick={this.switchTab('add')}>
                <span className="icon is-small">
                  <i className="fa fa-plus" />
                </span>
                <span className="is-hidden-mobile">Add RSVP</span>
                <span className="is-hidden-tablet">Add</span>
              </a>
            </li>
            <li className={Cx({ 'is-active': this.state.activeTab === 'message' })}>
              <a onClick={this.switchTab('message')}>
                <span className="icon is-small">
                  <i className="fa fa-envelope" />
                </span>
                <span className="is-hidden-mobile">Send message</span>
                <span className="is-hidden-tablet">Msg</span>
              </a>
            </li>
          </ul>
        </div>
        {this.rsvps()}
        <Modal
          title={`${this.state.rsvpModalTarget ? this.state.rsvpModalTarget.name : ''}`}
          isActive={!!this.state.rsvpModalTarget}
          onDismiss={this.onModalDismiss}
          onModalAction={
            this.state.rsvpModalTarget
              ? this.onDeleteRsvp(this.state.rsvpModalTarget.id!)(this.onModalDismiss)
              : undefined
          }
          modalActionClass="is-danger"
          modalActionText="Delete RSVP"
        >
          {this.rsvpDetails()}
        </Modal>
      </div>
    );
  }
}

function mapStateToProps(state: RootState): AdminDashboardStateProps {
  return {
    rsvps: state.firebase.data.rsvps,
  };
}

export default connect(mapStateToProps)(firebaseConnect(['rsvps'])(AdminDashboard));



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/adminDashboard.tsx